import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type { PropertyPageResponse } from '../../../types/property.types';
import type {
  Asset,
  CaptionSequenceResponse,
  DownloadAssetsParams,
  DownloadAssetsResult,
  Photoset,
  PhotosetWithAssets,
  SyncAssetsParams,
  SyncResponse
} from '../../../types/asset.types';
import type {
  StageImageRequest,
  StagingCommitRequest,
  VirtualStagingOptions,
  VirtualStagingRender
} from '../stage-with-ai/stagingTypes';

/** POST /assets/discard-uploads response (asset-service DiscardUploadsResponse). */
export interface DiscardUploadsResponse {
  discarded: string[];
  rejected: {
    assetUUID: string;
    reason:
      | 'OUT_OF_SCOPE'
      | 'NOT_UPLOADER'
      | 'TOO_OLD'
      | 'ALREADY_DELIVERED'
      | 'CHANGED_MEANWHILE';
  }[];
}

interface GetAssetsByOrderParams {
  orderUUID: string;
  type: string;
}

// Extracts the server-suggested filename from a Content-Disposition header.
// The backend sends both params (RFC 6266): `filename*=UTF-8''…` carries the
// real (percent-encoded) name, while `filename="…"` is the ASCII fallback with
// non-Latin-1 characters mangled to `_` — so prefer `filename*`. Exported for
// tests.
export function parseDispositionFilename(disposition: string): string | null {
  const extendedMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extendedMatch) {
    try {
      return decodeURIComponent(extendedMatch[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the plain param.
    }
  }
  const plainMatch = /filename="([^"]+)"/.exec(disposition);
  return plainMatch ? plainMatch[1] : null;
}

/**
 * DV-2524 §13: one shape for all three trash mutations — each carries `asset`,
 * the row it acts on, plus the `photosetUUID` its optimistic patch targets
 * (the `getAssetTrash` cache key; NOT part of any request URL — the endpoints
 * are addressed by `asset.assetUUID` alone). `trashAsset` appends `asset` as a
 * placeholder (built by `trash/toTrashedAsset` from the grid item the user
 * just deleted) until the server confirms; `recoverAsset` and
 * `permanentlyDeleteAsset` remove it immediately and — on failure — re-append
 * the same object via `appendToTrashListing`, the same helper `trashAsset`
 * uses for its forward patch. One type, not two, because every direction now
 * needs the full row, not just its id.
 */
export type TrashMutationArgs = { photosetUUID: string; asset: Asset };

/**
 * DV-2524 §13: drop a row from the cached trash listing so the Trash tab's
 * count badge and grid move on click rather than after two sequential round
 * trips (the mutation, then the invalidation-driven refetch). Naturally
 * idempotent — looking up an assetUUID that is not present is a no-op.
 */
const removeFromTrashListing = (assetUUID: string) => (draft: Asset[]) => {
  const at = draft.findIndex((asset) => asset.assetUUID === assetUUID);
  if (at !== -1) draft.splice(at, 1);
};

/**
 * DV-2524 §13. Idempotent by requirement, not by taste: a double-fired delete
 * is harmless today (the endpoint is idempotent and the refetch is
 * authoritative), but an unguarded append would add the row twice — badge
 * reading one too many, two cards sharing an assetUUID, duplicate React keys.
 * Same early-return as ImageAssetSlice's reinsertAsset and addRecovered.
 */
const appendToTrashListing = (asset: Asset) => (draft: Asset[]) => {
  if (draft.some((existing) => existing.assetUUID === asset.assetUUID)) return;
  draft.push(asset);
};

/** Backend `PublicLinkResponse`: the share token only; the UI builds the URL. */
export interface PublicLinkResponse {
  token: string;
  createdAt: string | null;
}

export const assetApi = createApi({
  reducerPath: 'assetApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Assets', 'AssetTrash'],
  endpoints: (builder) => ({
    /**
     * Single-property fetch via the search endpoint (the only way to look up
     * a property by UUID is through the paginated list with a `propertyId`
     * filter; backend returns the full `PropertyPageResponse` with one item
     * in `content[]`).
     */
    getPropertyById: builder.query<PropertyPageResponse, string>({
      query: (propertyUUID) =>
        `/property-service/api/v1/properties?propertyId=${propertyUUID}`
    }),

    getAssetsByOrder: builder.query<Asset[], GetAssetsByOrderParams>({
      query: ({ orderUUID, type }) =>
        `/asset-service/api/v1/assets?orderUUID=${orderUUID}&type=${type}`,
      providesTags: ['Assets']
    }),

    getPhotosets: builder.query<PhotosetWithAssets[], string>({
      query: (propertyUUID) =>
        `/asset-service/api/v1/assets/photosets?propertyUUID=${propertyUUID}`,
      providesTags: ['Assets']
    }),

    /**
     * DV-2854 / DV-2851: create the order's photoset, or link the order to an
     * existing one on the same property. ONE endpoint for both — omitting
     * `photosetId` means create.
     *
     * 201 = created, 200 = create-or-get returned the existing set, or a link
     * succeeded. The UI treats them IDENTICALLY; a 200 is never a warning.
     *
     * `displayDate` is the order date as the ROW rendered it, in the viewer's
     * timezone: the server names the photoset from it, and falling back to its
     * UTC default misnames every order placed in the evening by a day. Sent on
     * every CREATE, and on a link too where it is simply ignored. Optional
     * because a link needs no name at all — the target photoset already has
     * one — so an order with an unreadable date can still be linked, which is
     * the only path open to it.
     *
     * Invalidates Assets so the workspace refetches into the new set.
     */
    createOrLinkPhotoset: builder.mutation<
      Photoset,
      { orderUUID: string; displayDate?: string; photosetId?: string }
    >({
      query: ({ orderUUID, displayDate, photosetId }) => {
        const params = new URLSearchParams();
        if (displayDate) params.append('displayDate', displayDate);
        if (photosetId) params.append('photosetId', photosetId);
        return {
          url: `/asset-service/api/v1/assets/photosets/for-order/${orderUUID}?${params.toString()}`,
          method: 'POST'
        };
      },
      invalidatesTags: ['Assets']
    }),

    // ── DV-2221 edit suite. No cache invalidation on purpose: the page's
    // grid state lives in ImageAssetSlice (already updated optimistically by
    // the call sites), and usePropertyAssets loads via lazy triggers that a
    // tag refetch would not re-drive — invalidating would only churn network.
    updateAsset: builder.mutation<
      Asset,
      { assetUUID: string; body: { caption?: string; hidden?: boolean } }
    >({
      query: ({ assetUUID, body }) => ({
        url: `/asset-service/api/v1/assets/${assetUUID}`,
        method: 'PATCH',
        body
      })
    }),

    reorderPhotoset: builder.mutation<
      Photoset,
      { photosetId: string; orderedAssetUUIDs: string[] }
    >({
      query: ({ photosetId, orderedAssetUUIDs }) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/order`,
        method: 'PUT',
        body: { orderedAssetUUIDs }
      })
    }),

    /**
     * DV-2859: one-click Caption Sort. Server-side by contract (DV-2723 BE
     * handoff, 2026-09-13): no body — the server matches each photo's caption
     * to the Default Caption Sequence, stable-sorts, and applies the result
     * through the same reorder path a manual drag uses, so unsynced-changes
     * bookkeeping and Legacy propagation are identical. Returns the updated
     * photoset (same shape as `reorderPhotoset`). Already-sorted, 0- or
     * 1-photo sets come back unchanged with no error; customer roles get 403.
     */
    captionSortPhotoset: builder.mutation<Photoset, string>({
      query: (photosetId) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/caption-sort`,
        method: 'POST'
      })
    }),
    /**
     * DV-2861: set whether the client can see a photoset (DV-2860's flag).
     * Absolute, never a toggle — the body names the state to end in, so a
     * repeat or a stale view is a server no-op rather than a flip. Internal
     * roles only (403 otherwise); returns the updated photoset.
     *
     * Invalidates nothing on purpose: the asset page holds its photosets in
     * local state (usePropertyAssets), which a tag refetch would not reach,
     * and invalidating 'Assets' would refetch every other subscriber for
     * nothing. The caller writes the returned flag into that copy.
     */
    setPhotosetVisibility: builder.mutation<
      Photoset,
      { photosetId: string; visibleToClient: boolean }
    >({
      query: ({ photosetId, visibleToClient }) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/visibility`,
        method: 'PATCH',
        body: { visibleToClient }
      })
    }),

    /**
     * Public PhotoPlan gallery share link. Idempotent on the server: returns
     * the photoset's existing token or mints one. 400 (commons-core validation
     * envelope, reason in `code`) while the photoset is hidden from the client
     * or its order is not delivered; 404 for an unknown photoset. No tag
     * invalidation: the page adopts the returned token into its own photoset
     * copy (usePropertyAssets.setPhotosetPublicToken), the same field-only
     * merge the visibility PATCH uses.
     */
    createPublicLink: builder.mutation<PublicLinkResponse, string>({
      query: (photosetId) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/public-link`,
        method: 'POST'
      })
    }),

    /** Revokes the share link; the public URL 404s from the next load. */
    revokePublicLink: builder.mutation<void, string>({
      query: (photosetId) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/public-link`,
        method: 'DELETE'
      })
    }),

    /**
     * DV-2723: the company-wide Default Caption Sequence, any authenticated
     * user. Feeds the caption editor's ghost autocomplete (DV-2859 folded
     * this in) so a suggestion can never name a room Caption Sort does not
     * know. Cached for the session — it is 142 rows and changes rarely.
     */
    getDefaultCaptionSequence: builder.query<CaptionSequenceResponse, void>({
      query: () =>
        '/asset-service/api/v1/assets/system-config/default-caption-sequence'
    }),

    // photoUUIDs travel in the POST body, NOT the query string. "Download All"
    // enumerates every displayed photo's UUID (DV-2162), which on a 40+ photo
    // property produced a >2KB query string that the edge WAF blocks
    // (SizeRestrictions_QUERYSTRING) — surfacing to the browser as an opaque
    // cross-origin/CORS failure with no status. The scalar scope params stay in
    // the query string (bounded); only the unbounded UUID list moves to the body.
    downloadAssets: builder.mutation<DownloadAssetsResult, DownloadAssetsParams>({
      query: ({
        size,
        watermark,
        propertyUUID,
        orderUUID,
        photoUUIDs,
        includeHidden,
        aspectRatio
      }) => {
        const params = new URLSearchParams({
          size,
          watermark: String(watermark)
        });
        if (propertyUUID) params.append('propertyUUID', propertyUUID);
        if (orderUUID) params.append('orderUUID', orderUUID);
        if (includeHidden !== undefined)
          params.append('includeHidden', String(includeHidden));
        if (aspectRatio) params.append('aspectRatio', aspectRatio);
        return {
          url: `/asset-service/api/v1/assets/download?${params.toString()}`,
          method: 'POST',
          body: { photoUUIDs: photoUUIDs ?? [] },
          responseHandler: (response) => response.blob()
        };
      },
      // Old backends (and every multi-photo download) send application/zip; the
      // single-image branch sends image/*. Default unknown types to zip so a
      // missing header degrades to today's save-as-zip behavior.
      transformResponse: (blob: Blob, meta) => {
        const headers = meta?.response?.headers;
        const contentType = headers?.get('content-type') ?? '';
        const disposition = headers?.get('content-disposition') ?? '';
        return {
          blob,
          isZip: !contentType.startsWith('image/'),
          suggestedFilename: parseDispositionFilename(disposition)
        };
      }
    }),

    // POST to /asset-service/.../photosets/{photosetId}/trigger-webhook. The
    // backend fans the webhook payload (active photos + media links) out to
    // every active custom webhook endpoint registered on the entity.
    // Invalidates the Assets cache so a follow-up photoset fetch surfaces the
    // freshly updated `lastWebhookTriggeredAt` timestamp on the next modal
    // open.
    triggerWebhook: builder.mutation<void, string>({
      query: (photosetId) => ({
        url: `/asset-service/api/v1/assets/photosets/${photosetId}/trigger-webhook`,
        method: 'POST'
      }),
      invalidatesTags: ['Assets']
    }),

    // photoUUIDs travel in the POST body, NOT the query string — see downloadAssets
    // above. "Sync All" enumerates every displayed photo's UUID, which on a large
    // photoset overflowed the edge WAF's query-string size limit (SizeRestrictions_
    // QUERYSTRING) and was blocked before reaching the service.
    syncAssets: builder.mutation<SyncResponse, SyncAssetsParams>({
      query: ({ watermarked, propertyUUID, orderUUID, photoUUIDs }) => {
        const params = new URLSearchParams({
          watermarked: String(watermarked)
        });
        if (propertyUUID) params.append('propertyUUID', propertyUUID);
        if (orderUUID) params.append('orderUUID', orderUUID);
        return {
          url: `/property-service/api/v1/properties/assets/sync?${params.toString()}`,
          method: 'POST',
          body: { photoUUIDs: photoUUIDs ?? [] }
        };
      },
      invalidatesTags: ['Assets']
    }),

    // ── Virtual staging (DV-2252) — generate side is LIVE. Every start /
    // regenerate is a real VSAI vendor call + S3 write; getStatus is a cheap
    // Mongo read (status advances via webhook/reconciler, not on read).
    getVirtualStagingOptions: builder.query<VirtualStagingOptions, void>({
      query: () => '/asset-service/api/v1/assets/virtual-staging/options'
    }),

    startVirtualStaging: builder.mutation<
      VirtualStagingRender,
      { assetUUID: string; request: StageImageRequest }
    >({
      query: ({ assetUUID, request }) => ({
        url: `/asset-service/api/v1/assets/${assetUUID}/virtual-staging`,
        method: 'POST',
        body: request
      })
    }),

    getVirtualStagingStatus: builder.query<VirtualStagingRender, string>({
      query: (renderRecordId) =>
        `/asset-service/api/v1/assets/virtual-staging/${renderRecordId}`
    }),

    // DV-2252 regenerate-after-staged: recover the render record for a photo
    // that was committed in an earlier session. 404 = not a committed AI
    // render (e.g. a staged UPLOAD) — surfaces to the caller, no logout.
    getVirtualStagingByAsset: builder.query<VirtualStagingRender, string>({
      query: (assetUUID) =>
        `/asset-service/api/v1/assets/virtual-staging/by-asset/${assetUUID}`
    }),

    regenerateVirtualStaging: builder.mutation<
      VirtualStagingRender,
      { renderRecordId: string; request: StageImageRequest }
    >({
      query: ({ renderRecordId, request }) => ({
        url: `/asset-service/api/v1/assets/virtual-staging/${renderRecordId}/regenerate`,
        method: 'POST',
        body: request
      })
    }),

    // Commit a DONE render into a real Assets doc attached to the photoset
    // (DV-2252). Returns the persisted asset with real ids. Idempotent
    // server-side. NOT tag-invalidating: the working set is reconciled locally
    // from the response so in-flight unsynced edits aren't clobbered by a
    // refetch.
    commitVirtualStaging: builder.mutation<
      Asset,
      { renderRecordId: string; request: StagingCommitRequest }
    >({
      query: ({ renderRecordId, request }) => ({
        url: `/asset-service/api/v1/assets/virtual-staging/${renderRecordId}/commit`,
        method: 'POST',
        body: request
      })
    }),

    // ── DV-2524 trash / recover / permanent delete.
    //
    // Internal-only by UI gate ALONE: canDeleteAssets() (non-prod origin AND
    // INTERNAL_ADMIN) is the only thing keeping these off a customer's screen.
    // asset-service has no @PreAuthorize / hasRole / hasAuthority anywhere in
    // src/main — its only server-side check is assertAssetWithinEntityScope,
    // which enforces ENTITY SCOPE, not role. So any customer token that owns the
    // entity can call all four. Do not relax the frontend gate on the assumption
    // that a server-side role backstop exists; there isn't one.
    //
    // All four are photoset-scoped server-side: asset-service resolves the
    // caller's entity through the asset's photoset (or, once trashed, its
    // trashedFromPhotosetUUID), so a photoset-less asset 400s. Call sites must
    // already be gated on photoset context.
    //
    // Only 'AssetTrash' is invalidated, never 'Assets': the grid's working set
    // lives in ImageAssetSlice and is updated optimistically by useAssetTrash,
    // and usePropertyAssets loads through lazy triggers a tag refetch would not
    // re-drive — same reasoning as the DV-2221 edit endpoints above.
    getAssetTrash: builder.query<Asset[], string>({
      query: (photosetUUID) =>
        `/asset-service/api/v1/assets/trash?photosetUUID=${photosetUUID}`,
      providesTags: ['AssetTrash']
    }),

    // Upload modal cancel (asset-service PR #252): discard photos uploaded and
    // never confirmed. Removes each from its photoset and mirrors the delete to
    // Legacy; not listed in the trash, not recoverable. 200 with a per-photo
    // outcome. Called through upload/uploadAssetService.discardUploads, which
    // owns batching and retries. No tags: the grid
    // never received these photos (see usePropertyAssets on lazy triggers).
    discardUploads: builder.mutation<DiscardUploadsResponse, string[]>({
      query: (assetUUIDs) => ({
        url: '/asset-service/api/v1/assets/discard-uploads',
        method: 'POST',
        body: { assetUUIDs }
      })
    }),

    // ⚠️ PATH-VARIABLE form only. The older DELETE /assets?assetUUID=&type=
    // hard-deletes with no recovery path and never tells Legacy, so the UI must
    // not call it. Uploads the user abandoned before confirming go through
    // upload/uploadAssetService.discardUploads (see the JSDoc there).
    // Idempotent server-side: deleting an already-trashed asset is a no-op
    // repair, so a double-click is harmless.
    trashAsset: builder.mutation<Asset, TrashMutationArgs>({
      query: ({ asset }) => ({
        url: `/asset-service/api/v1/assets/${asset.assetUUID}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AssetTrash'],
      async onQueryStarted(
        { photosetUUID, asset },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          assetApi.util.updateQueryData(
            'getAssetTrash',
            photosetUUID,
            appendToTrashListing(asset)
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // NOT patch.undo() — deliberately. A bulk delete fires every photo's
          // trashAsset synchronously (Promise.all in useAssetTrash.trashPhotos),
          // so several appends can land on this cache entry before any of them
          // settle. Immer's inverse patch for Array.push is positional
          // (`{op:'replace', path:['length'], value:<length-before-this-push>}`),
          // captured at patch-creation time. Undoing a patch that is not the
          // most-recently-applied one truncates the array back to that stale
          // length — silently discarding every element pushed *after* it, even
          // ones whose own request succeeded. Confirmed via produceWithPatches:
          // three pushes onto [] then undoing only the middle one drops the
          // third element too. removeFromTrashListing looks the row up by
          // assetUUID against CURRENT state instead of replaying a stale
          // position, so it can't clobber a sibling append. Do not "simplify"
          // this back to patch.undo() — see DV-2524 §13.
          //
          // Guarded on patch.patches.length: appendToTrashListing early-returns
          // (leaving `patches` empty) when this call's own assetUUID was
          // already in the draft — reachable on a double-fired delete whose
          // first call already succeeded. Removing unconditionally here would
          // then delete a row this call never added.
          if (patch.patches.length > 0) {
            dispatch(
              assetApi.util.updateQueryData(
                'getAssetTrash',
                photosetUUID,
                removeFromTrashListing(asset.assetUUID)
              )
            );
          }
        }
      }
    }),

    // Re-attaches to the photoset it came from at max(sortOrder) + 1. 400s when
    // the asset is not trashed, or its origin photoset no longer exists.
    recoverAsset: builder.mutation<Asset, TrashMutationArgs>({
      query: ({ asset }) => ({
        url: `/asset-service/api/v1/assets/${asset.assetUUID}/recover`,
        method: 'POST'
      }),
      invalidatesTags: ['AssetTrash'],
      async onQueryStarted(
        { photosetUUID, asset },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          assetApi.util.updateQueryData(
            'getAssetTrash',
            photosetUUID,
            removeFromTrashListing(asset.assetUUID)
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // NOT patch.undo() — deliberately, mirroring trashAsset above, and
          // for the mirror-image reason. Restore has no confirm dialog (the
          // click wires straight to recoverPhoto) and busyAssetUUID is a
          // per-row scalar that never disables a second row, and the row now
          // vanishes on click — so two fast Restore clicks on different rows
          // routinely put two recoverAsset calls in flight against this same
          // cache entry, no bulk action required. Immer's inverse patch for a
          // splice-remove is positional too: undoing the first-submitted
          // removal after a second one has also landed can resurrect the
          // second row — one that left correctly and may already be back in
          // the Photos grid via addRecovered, so it would render in both
          // places and the badge would over-count. appendToTrashListing is
          // content-addressed and already idempotent, so it re-adds only this
          // call's own row. Re-appending puts the row at the end rather than
          // its original index — immaterial, §13 already establishes listing
          // position is not meaningful and the refetch reconciles.
          //
          // The row reappearing at all is itself accepted (§7): a 400 body
          // cannot distinguish "already restored" (row should stay gone) from
          // "origin photoset gone" (row should still show as trashed), so
          // suppressing the re-append would sometimes drop a row that should
          // stay. The invalidation's refetch (RTK 1.9.7 runs it on rejection
          // too) reconciles either way.
          //
          // Guarded on patch.patches.length for the same reason as trashAsset:
          // if removeFromTrashListing was already a no-op (this row was gone
          // before this call — e.g. a sibling recover on the same row got
          // there first), this call removed nothing, so its own failure owes
          // nothing back.
          if (patch.patches.length > 0) {
            dispatch(
              assetApi.util.updateQueryData(
                'getAssetTrash',
                photosetUUID,
                appendToTrashListing(asset)
              )
            );
          }
        }
      }
    }),

    // 204. Only valid on a trashed asset — a live photo 400s, so the trash is
    // the only road to permanent deletion.
    permanentlyDeleteAsset: builder.mutation<void, TrashMutationArgs>({
      query: ({ asset }) => ({
        url: `/asset-service/api/v1/assets/${asset.assetUUID}/permanent`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AssetTrash'],
      async onQueryStarted(
        { photosetUUID, asset },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          assetApi.util.updateQueryData(
            'getAssetTrash',
            photosetUUID,
            removeFromTrashListing(asset.assetUUID)
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Same reasoning as recoverAsset immediately above — see its
          // comment. Two fast Delete Permanently clicks on different rows are
          // just as reachable as two fast Restores.
          if (patch.patches.length > 0) {
            dispatch(
              assetApi.util.updateQueryData(
                'getAssetTrash',
                photosetUUID,
                appendToTrashListing(asset)
              )
            );
          }
        }
      }
    })
  })
});

// Shared RTK Query fixedCacheKeys so the assets page header can observe the
// in-flight state of syncs triggered inside the Sync modal (the
// callout's refresh icon spins for exactly the duration of the request).
// Any component using these mutations with the same key shares one
// mutation cache entry.
export const PMS_SYNC_CACHE_KEY = 'sync-and-share/pms-sync';
export const WEBHOOK_PUSH_CACHE_KEY = 'sync-and-share/webhook-push';

export const {
  useGetPropertyByIdQuery,
  useLazyGetPropertyByIdQuery,
  useGetAssetsByOrderQuery,
  useLazyGetAssetsByOrderQuery,
  useGetPhotosetsQuery,
  useLazyGetPhotosetsQuery,
  useCreateOrLinkPhotosetMutation,
  useUpdateAssetMutation,
  useReorderPhotosetMutation,
  useCaptionSortPhotosetMutation,
  useSetPhotosetVisibilityMutation,
  useCreatePublicLinkMutation,
  useRevokePublicLinkMutation,
  useGetDefaultCaptionSequenceQuery,
  useDownloadAssetsMutation,
  useSyncAssetsMutation,
  useTriggerWebhookMutation,
  useGetVirtualStagingOptionsQuery,
  useStartVirtualStagingMutation,
  useLazyGetVirtualStagingStatusQuery,
  useLazyGetVirtualStagingByAssetQuery,
  useRegenerateVirtualStagingMutation,
  useCommitVirtualStagingMutation,
  useGetAssetTrashQuery,
  useTrashAssetMutation,
  useRecoverAssetMutation,
  usePermanentlyDeleteAssetMutation
} = assetApi;
